/**
 * Prompt Builder Configuration
 * All options for the visual prompt builder
 */

// ============================================================================
// TYPES
// ============================================================================

export interface PromptOption {
  id: string
  label: string
  icon?: string
  preview?: string
  promptText: string
}

export interface PromptSection {
  id: string
  label: string
  options: PromptOption[]
  multiSelect?: boolean
  clearable?: boolean
}

export interface SliderSection {
  id: string
  label: string
  min: number
  max: number
  step: number
  unit: string
  defaultValue: number
  promptPrefix: string
}

export interface GeneratedPrompt {
  id: string
  text: string
  category: string
  isAdded: boolean
}

// ============================================================================
// LAYOUT OPTIONS
// ============================================================================

export const LAYOUT_TYPES: PromptOption[] = [
  { id: "hero", label: "Hero", promptText: "Create a hero layout" },
  { id: "features", label: "Features", promptText: "Create a features layout" },
  { id: "onboarding", label: "Onboarding", promptText: "Create an onboarding layout" },
  { id: "docs", label: "Docs", promptText: "Create a docs layout" },
  { id: "updates", label: "Updates", promptText: "Create a updates layout" },
  { id: "portfolio", label: "Portfolio", promptText: "Create a portfolio layout" },
]

export const LAYOUT_CONFIGURATIONS: PromptOption[] = [
  { id: "single", label: "Single", promptText: "Layout Configuration: Single column" },
  { id: "1-2", label: "1-2", promptText: "Layout Configuration: 1-2 split (one third, two thirds)" },
  { id: "2-1", label: "2-1", promptText: "Layout Configuration: 2-1 split (two thirds, one third)" },
  {
    id: "2-2",
    label: "2-2 Square",
    promptText: "Layout Configuration: 2-2 Square (four equal-sized cells in a square grid)",
  },
  { id: "3-col", label: "3 Column", promptText: "Layout Configuration: 3 equal columns" },
  { id: "4-col", label: "4 Column", promptText: "Layout Configuration: 4 equal columns" },
  { id: "masonry", label: "Masonry", promptText: "Layout Configuration: Masonry grid layout" },
  { id: "bento", label: "Bento", promptText: "Layout Configuration: Bento box grid layout" },
]

// ============================================================================
// STYLE OPTIONS
// ============================================================================

export const STYLES: PromptOption[] = [
  { id: "flat", label: "Flat", promptText: "Use flat style design" },
  { id: "outline", label: "Outline", promptText: "Use outline style design" },
  { id: "minimalist", label: "Minimalist", promptText: "Use minimalist style design" },
  { id: "glass", label: "Glass", promptText: "Use glassmorphism style design" },
  { id: "ios", label: "iOS", promptText: "Use iOS style design" },
  { id: "material", label: "Material", promptText: "Use Material Design style" },
]

export const THEMES: PromptOption[] = [
  { id: "light", label: "Light Mode", promptText: "Use light mode theme" },
  { id: "dark", label: "Dark Mode", promptText: "Use dark mode theme" },
]

export const ACCENT_COLORS: PromptOption[] = [
  { id: "primary", label: "Primary", promptText: "Use primary accent color" },
  { id: "blue", label: "Blue", promptText: "Use blue accent color" },
  { id: "indigo", label: "Indigo", promptText: "Use indigo accent color" },
  { id: "violet", label: "Violet", promptText: "Use violet accent color" },
  { id: "purple", label: "Purple", promptText: "Use purple accent color" },
  { id: "fuchsia", label: "Fuchsia", promptText: "Use fuchsia accent color" },
]

export const BACKGROUND_COLORS: PromptOption[] = [
  { id: "transparent", label: "Transparent", promptText: "Use transparent background" },
  { id: "neutral", label: "Neutral", promptText: "Use neutral background color" },
  { id: "gray", label: "Gray", promptText: "Use gray background color" },
  { id: "slate", label: "Slate", promptText: "Use slate background color" },
  { id: "zinc", label: "Zinc", promptText: "Use zinc background color" },
  { id: "stone", label: "Stone", promptText: "Use stone background color" },
]

export const BORDER_COLORS: PromptOption[] = [
  { id: "transparent", label: "Transparent", promptText: "Use transparent borders" },
  { id: "neutral", label: "Neutral", promptText: "Use neutral border color" },
  { id: "gray", label: "Gray", promptText: "Use gray border color" },
  { id: "slate", label: "Slate", promptText: "Use slate border color" },
  { id: "zinc", label: "Zinc", promptText: "Use zinc border color" },
  { id: "stone", label: "Stone", promptText: "Use stone border color" },
]

export const SHADOWS: PromptOption[] = [
  { id: "none", label: "None", promptText: "Use no shadows" },
  { id: "small", label: "Small", promptText: "Use small shadows" },
  { id: "medium", label: "Medium", promptText: "Use medium shadows" },
  { id: "large", label: "Large", promptText: "Use large shadows" },
  { id: "xl", label: "Extra Large", promptText: "Use extra large shadows" },
  { id: "xxl", label: "XXL", promptText: "Use XXL shadows" },
]

// ============================================================================
// TYPOGRAPHY OPTIONS
// ============================================================================

export const TYPEFACE_FAMILIES: PromptOption[] = [
  { id: "sans", label: "Sans", promptText: "Use sans-serif typeface family" },
  { id: "serif", label: "Serif", promptText: "Use serif typeface family" },
  { id: "monospace", label: "Monospace", promptText: "Use monospace typeface family" },
  { id: "condensed", label: "Condensed", promptText: "Use condensed typeface family" },
  { id: "expanded", label: "Expanded", promptText: "Use expanded typeface family" },
  { id: "rounded", label: "Rounded", promptText: "Use rounded typeface family" },
]

export const HEADING_FONTS: PromptOption[] = [
  { id: "inter", label: "Inter", promptText: "Use Inter for headings" },
  { id: "geist", label: "Geist", promptText: "Use Geist for headings" },
  { id: "manrope", label: "Manrope", promptText: "Use Manrope for headings" },
  { id: "playfair", label: "Playfair Display", promptText: "Use Playfair Display for headings" },
  { id: "instrument", label: "Instrument Serif", promptText: "Use Instrument Serif for headings" },
  { id: "plex", label: "Plex Serif", promptText: "Use IBM Plex Serif for headings" },
]

export const BODY_FONTS: PromptOption[] = [
  { id: "inter", label: "Inter", promptText: "Use Inter for body text" },
  { id: "geist", label: "Geist", promptText: "Use Geist for body text" },
  { id: "manrope", label: "Manrope", promptText: "Use Manrope for body text" },
  { id: "playfair", label: "Playfair Display", promptText: "Use Playfair Display for body text" },
  { id: "instrument", label: "Instrument Serif", promptText: "Use Instrument Serif for body text" },
  { id: "plex", label: "Plex Serif", promptText: "Use IBM Plex Serif for body text" },
]

export const HEADING_SIZES: PromptOption[] = [
  { id: "small", label: "20-32px", promptText: "Use small heading size (20-32px)" },
  { id: "medium", label: "32-40px", promptText: "Use medium heading size (32-40px)" },
  { id: "large", label: "48-64px", promptText: "Use large heading size (48-64px)" },
  { id: "xl", label: "64-80px", promptText: "Use extra large heading size (64-80px)" },
]

export const SUBHEADING_SIZES: PromptOption[] = [
  { id: "small", label: "16-20px", promptText: "Use small subheading size (16-20px)" },
  { id: "medium", label: "20-24px", promptText: "Use medium subheading size (20-24px)" },
  { id: "large", label: "24-28px", promptText: "Use large subheading size (24-28px)" },
  { id: "xl", label: "28-32px", promptText: "Use extra large subheading size (28-32px)" },
]

export const BODY_TEXT_SIZES: PromptOption[] = [
  { id: "small", label: "12-14px", promptText: "Use small body text size (12-14px)" },
  { id: "medium", label: "14-16px", promptText: "Use medium body text size (14-16px)" },
  { id: "large", label: "16-18px", promptText: "Use large body text size (16-18px)" },
  { id: "xl", label: "18-20px", promptText: "Use extra large body text size (18-20px)" },
]

export const HEADING_LETTER_SPACING: PromptOption[] = [
  { id: "tighter", label: "Tighter", promptText: "Apply tighter letter spacing to headings" },
  { id: "tight", label: "Tight", promptText: "Apply tight letter spacing to headings" },
  { id: "normal", label: "Normal", promptText: "Apply normal letter spacing to headings" },
  { id: "wide", label: "Wide", promptText: "Apply wide letter spacing to headings" },
  { id: "wider", label: "Wider", promptText: "Apply wider letter spacing to headings" },
  { id: "widest", label: "Widest", promptText: "Apply widest letter spacing to headings" },
]

// ============================================================================
// ANIMATION OPTIONS
// ============================================================================

export const ANIMATION_TYPES: PromptOption[] = [
  { id: "fade", label: "Fade", promptText: "Use fade animation" },
  { id: "slide", label: "Slide", promptText: "Use slide animation" },
  { id: "scale", label: "Scale", promptText: "Use scale animation" },
  { id: "rotate", label: "Rotate", promptText: "Use rotate animation" },
  { id: "blur", label: "Blur", promptText: "Use blur animation" },
  { id: "3d", label: "3D", promptText: "Use 3D animation" },
]

export const ANIMATION_SCENES: PromptOption[] = [
  { id: "all", label: "All at once", promptText: "Animate all at once" },
  { id: "sequence", label: "Sequence", promptText: "Animate in sequence" },
  { id: "word", label: "Word by word", promptText: "Animate word by word" },
  { id: "letter", label: "Letter by letter", promptText: "Animate letter by letter" },
]

export const TIMING_FUNCTIONS: PromptOption[] = [
  { id: "linear", label: "Linear", promptText: "Use linear easing" },
  { id: "ease", label: "Ease", promptText: "Use ease easing" },
  { id: "ease-in", label: "Ease In", promptText: "Use ease in easing" },
  { id: "ease-out", label: "Ease Out", promptText: "Use ease out easing" },
  { id: "ease-in-out", label: "Ease In Out", promptText: "Use ease in out easing" },
  { id: "spring", label: "Spring", promptText: "Use spring easing" },
]

export const ITERATIONS: PromptOption[] = [
  { id: "once", label: "Once", promptText: "Play animation once" },
  { id: "twice", label: "Twice", promptText: "Play animation twice" },
  { id: "thrice", label: "Thrice", promptText: "Repeat animation thrice" },
  { id: "infinite", label: "Infinite", promptText: "Loop animation infinitely" },
]

export const DIRECTIONS: PromptOption[] = [
  { id: "normal", label: "Normal", promptText: "Animation direction: normal" },
  { id: "reverse", label: "Reverse", promptText: "Animation direction: reverse" },
  { id: "alternate", label: "Alternate", promptText: "Animation direction: alternate" },
  { id: "alternate-reverse", label: "Alternate Rev...", promptText: "Animation direction: alternate-reverse" },
]

// ============================================================================
// SLIDER CONFIGS
// ============================================================================

export const DURATION_SLIDER: SliderSection = {
  id: "duration",
  label: "Duration",
  min: 0.1,
  max: 3,
  step: 0.1,
  unit: "s",
  defaultValue: 0.8,
  promptPrefix: "Animation duration:",
}

export const DELAY_SLIDER: SliderSection = {
  id: "delay",
  label: "Delay",
  min: 0,
  max: 2,
  step: 0.1,
  unit: "s",
  defaultValue: 0,
  promptPrefix: "Animation delay:",
}

// ============================================================================
// SECTION CONFIGS
// ============================================================================

export const PROMPT_SECTIONS: PromptSection[] = [
  { id: "layoutType", label: "LAYOUT TYPE", options: LAYOUT_TYPES },
  { id: "layoutConfig", label: "LAYOUT CONFIGURATION", options: LAYOUT_CONFIGURATIONS },
  { id: "style", label: "STYLE", options: STYLES },
  { id: "theme", label: "THEME", options: THEMES },
  { id: "accentColor", label: "ACCENT COLOR", options: ACCENT_COLORS },
  { id: "backgroundColor", label: "BACKGROUND COLOR", options: BACKGROUND_COLORS },
  { id: "borderColor", label: "BORDER COLOR", options: BORDER_COLORS },
  { id: "shadow", label: "SHADOW", options: SHADOWS },
  { id: "typefaceFamily", label: "TYPEFACE FAMILY", options: TYPEFACE_FAMILIES, clearable: true },
  { id: "headingFont", label: "HEADING FONT", options: HEADING_FONTS, clearable: true },
  { id: "bodyFont", label: "BODY & UI FONT", options: BODY_FONTS, clearable: true },
  { id: "headingSize", label: "HEADING SIZE", options: HEADING_SIZES, clearable: true },
  { id: "subheadingSize", label: "SUBHEADING SIZE", options: SUBHEADING_SIZES, clearable: true },
  { id: "bodyTextSize", label: "BODY TEXT SIZE", options: BODY_TEXT_SIZES, clearable: true },
  { id: "headingLetterSpacing", label: "HEADING LETTER SPACING", options: HEADING_LETTER_SPACING, clearable: true },
  { id: "animationType", label: "ANIMATION TYPE", options: ANIMATION_TYPES, multiSelect: true, clearable: true },
  { id: "animationScene", label: "SCENE", options: ANIMATION_SCENES, clearable: true },
  { id: "timing", label: "TIMING", options: TIMING_FUNCTIONS, clearable: true },
  { id: "iterations", label: "ITERATIONS", options: ITERATIONS, clearable: true },
  { id: "direction", label: "DIRECTION", options: DIRECTIONS, clearable: true },
]

// ============================================================================
// TEMPLATE CATEGORIES
// ============================================================================

export const TEMPLATE_CATEGORIES = [
  { id: "all", label: "All" },
  { id: "web", label: "Web" },
  { id: "mobile", label: "Mobile" },
  { id: "animation", label: "Animation" },
  { id: "login", label: "Login" },
  { id: "sidebar", label: "Sidebar" },
  { id: "onboarding", label: "Onboarding" },
  { id: "grid", label: "Grid" },
]

// ============================================================================
// DEVICE TOGGLES
// ============================================================================

export const DEVICE_TYPES = [
  { id: "web", label: "Web" },
  { id: "mobile", label: "Mobile" },
]
